/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CommandButton } from '@fluentui/react/lib/Button';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { Toast } from 'src/luis/projects/events/components/Toast/Toast';
import { NoToastsMessage, ToastPanelActionBar, ToastPanelContainer } from 'src/luis/projects/events/components/ToastPanel/toastPanelStyles';

const messages = defineMessages({
    noToasts: {
        id: 'trackers.toastPanel.noToasts',
        defaultMessage: 'No new notifications from this session'
    },
    dismissAll: {
        id: 'trackers.toastPanel.dismissAll',
        defaultMessage: 'Dismiss All'
    },
    showMoreOptionsLabel: {
        id: 'trackers.toastPanel.showMoreOptionsLabel',
        defaultMessage: 'Show more options'
    }
});

/**
 * Renders a panel to display the toasts in the system.
 */
export const ToastPanel = observer(() => {
    const { rootStore, dispatcher } = useGlobalState();
    const { localize } = useLocalization();

    const toastStores = rootStore.toastStoreHouse.items;

    /**
     * Dismisses the toast with the given id.
     *
     * @param id The id of the toast to dismiss.
     */
    const onDismiss = (id: string) => {
        dispatcher.dispatch('removeToast', { id });
    };

    /**
     * Dismisses all toasts.
     */
    const onDismissAll = () => {
        dispatcher.dispatch('removeAllToasts');
    };

    return (
        <ToastPanelContainer>
            <ToastPanelActionBar horizontal horizontalAlign="end" verticalAlign="center">
                <CommandButton onClick={onDismissAll} disabled={!toastStores.length}>
                    {localize(messages.dismissAll)}
                </CommandButton>
            </ToastPanelActionBar>

            {toastStores.length ? (
                <TransitionGroup>
                    {toastStores.map(toastStore => (
                        <CSSTransition key={toastStore.id} classNames="toast" timeout={250}>
                            <Toast onDismiss={onDismiss} toast={toastStore} />
                        </CSSTransition>
                    ))}
                </TransitionGroup>
            ) : (
                <NoToastsMessage variant="large" block>
                    {localize(messages.noToasts)}
                </NoToastsMessage>
            )}
        </ToastPanelContainer>
    );
});
